import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalizationContext } from '@sharesight/gatsby-plugin-sharesight-localization';

import { useContentfulLocales } from 'hooks/useContentfulLocales';
import { sortLocales } from 'helpers/sortLocales';
import { getLocalizedPath } from 'helpers/url';

import { BaseButton } from 'components/Button';
import { FlexibleLinkComponent, PartnerLink } from 'components/Links';

import { useCookiePreferences } from 'components/CookiePreferences/CookiePreferencesProvider';
import { COOKIE_COUNTRY } from 'components/Cookie/SharesightCountryCookie/constants';
import { useCookies } from 'hooks/useCookies';
import { triggerReload } from 'helpers/triggerReload';
import { LocaleContainer } from './LocaleContainer.styled';
import { StyledLocaleLink } from './StyledLocaleLink.styled';

import type { FC } from 'react';
import type { Locale } from '@sharesight/gatsby-plugin-sharesight-localization';
import type { ContentfulLocale } from 'hooks/useContentfulLocales';

export const LocalesFooter: FC<{}> = () => {
  const { basePath, currentLocale, defaultLocale } = useLocalizationContext();
  const locales = useContentfulLocales();
  const cookiePreferences = useCookiePreferences();
  const [shouldReload, setShouldReload] = useState(false);

  const { cookies, setCookie, removeCookie } = useCookies();

  const setLocale = useCallback(
    ({ id }: Locale) => {
      // GDPR: only sets the sharesight_country cookie if functionality cookies are accepted or the user is on au/nz locale
      if (cookiePreferences?.functionality) {
        setCookie(COOKIE_COUNTRY, id, { path: '/' });
      }
    },
    [setCookie, cookiePreferences?.functionality]
  );

  const handleOnClick = useCallback(
    (locale: Locale) => {
      if (cookiePreferences?.setShouldInitialize) {
        cookiePreferences.setShouldInitialize(true);
      }
      setLocale(locale);

      // reloads when the locale is manually changed to refresh the cookies and gtm tags based on the new locale
      setShouldReload(true);
    },
    [setLocale, cookiePreferences]
  );

  const localesInOrder = useMemo(
    () => sortLocales<ContentfulLocale>(locales, defaultLocale),
    [locales, defaultLocale]
  );

  const linkType = useMemo(() => {
    if (basePath.includes('/partners')) return PartnerLink;
    return FlexibleLinkComponent;
  }, [basePath]);

  const renderLocaleLinks = useMemo(
    () =>
      localesInOrder.map(locale => {
        const link = getLocalizedPath({ path: basePath, locale });

        const isCurrentLocale = currentLocale.id === locale.id;

        return (
          <StyledLocaleLink
            as={linkType}
            key={locale.id}
            asButton
            onClick={() => handleOnClick(locale)}
            appearance="link"
            containerBackground="negative"
            to={link}
            toLocale={locale}
            isCurrentLocale={isCurrentLocale}
          >
            {locale.translations.country || 'Global'}
          </StyledLocaleLink>
        );
      }),
    [basePath, currentLocale.id, localesInOrder, handleOnClick, linkType]
  );

  useEffect(() => {
    if (shouldReload) {
      setShouldReload(false);
      triggerReload();
    }
  }, [cookiePreferences?.functionality, currentLocale.id, removeCookie, cookies, shouldReload]);

  return (
    <LocaleContainer>
      <BaseButton as="div" color="transparent" textColor="white" noUnderline>
        Switch region:
      </BaseButton>

      {renderLocaleLinks}
    </LocaleContainer>
  );
};
